import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BackButton from "../Button/BackButton";
import useBiddingStore from "./useBiddingStore";
import BidsHelpModal from "./BidsHelpModal";
import { LS_KEYS } from "utils/constants/localStorageKeys";
import {
  PageWrapper,
  TopBar,
  TopBarTitle,
  HelpButton,
  ListContainer,
  BidCard,
  IconBox,
  IconWrapper,
  Badge,
  CardBody,
  CardTitle,
  CardSubtitle,
  ChevronRight,
} from "./styles.bids";

const getLocalDateKey = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${now.getFullYear()}-${month}-${day}`;
};

const getTokenHash = (token) => {
  let hash = 0;

  for (let i = 0; i < token.length; i += 1) {
    hash = ((hash << 5) - hash + token.charCodeAt(i)) | 0;
  }

  return `${token.length}:${Math.abs(hash).toString(36)}`;
};

const getBidsHelpBannerStorageKey = () => {
  const token = window.localStorage.getItem(LS_KEYS.TOKEN);

  if (!token) return null;

  return `${LS_KEYS.BIDS_HELP_BANNER_LAST_SHOWN_ON}:${getTokenHash(token)}`;
};

const formatOpportunityAmount = (amount) => {
  const value = Number(amount);

  if (!Number.isFinite(value) || value <= 0) return null;

  const compactValue = (num) => Number(num.toFixed(1)).toString();

  if (value >= 10000000) return `₹${compactValue(value / 10000000)}Cr`;
  if (value >= 100000) return `₹${compactValue(value / 100000)}L`;
  if (value >= 1000) return `₹${compactValue(value / 1000)}K`;

  return `₹${compactValue(value)}`;
};

const BID_ITEMS = [
  { id: "live",      title: "Live bids",      countKey: "live",               icon: <img src="/logos/bids_live.svg"      width="24" height="24" alt="" /> },
  { id: "upcoming",  title: "Upcoming bids",  countKey: "upcoming",           icon: <img src="/logos/bids_upcoming.svg"  width="24" height="24" alt="" /> },
  { id: "awarded",   title: "Awarded bids",   countKey: "awarded",            icon: <img src="/logos/bids_awarded.svg"   width="24" height="24" alt="" /> },
  { id: "submitted", title: "Submitted bids", countKey: "with_submitted_bids",icon: <img src="/logos/bids_submitted.svg" width="24" height="24" alt="" /> },
  { id: "previous",  title: "Previous bids",  countKey: "previous",           icon: <img src="/logos/bids_previous.svg"  width="24" height="24" alt="" /> },
  { id: "all",       title: "All bids",       countKey: "all",                icon: <img src="/logos/bids_all.svg"       width="24" height="24" alt="" /> },
];

const BidsFilterPage = () => {
  const navigate = useNavigate();
  const { sessionCounts, getSessionCounts, fetchBiddingTerms, termsConfig } = useBiddingStore();
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    getSessionCounts();
    fetchBiddingTerms();
  }, []);

  useEffect(() => {
    const storageKey = getBidsHelpBannerStorageKey();
    const today = getLocalDateKey();

    if (!storageKey || window.localStorage.getItem(storageKey) === today) return;

    window.localStorage.setItem(storageKey, today);
    setShowHelp(true);
  }, []);

  return (
    <PageWrapper>
      <TopBar>
        <BackButton pathName={"/"} />
        <TopBarTitle>Bids</TopBarTitle>
        <HelpButton onClick={() => navigate("/bids/faq")} aria-label="Help">
          <img src="/logos/bids_help.svg" width="20" height="20" alt="" />
        </HelpButton>
      </TopBar>

      <BidsHelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} audioUrl={termsConfig.audioUrl} />

      <ListContainer>
        {BID_ITEMS.map((item) => {
          const shouldShowCount = ["live", "upcoming"].includes(item.id);
          const count = item.id === "all"
            ? (sessionCounts.live || 0) +
              (sessionCounts.upcoming || 0) +
              (sessionCounts.awarded || 0) +
              (sessionCounts.previous || 0) +
              (sessionCounts.with_submitted_bids || 0)
            : item.countKey ? (sessionCounts[item.countKey] || 0) : 0;
          const opportunityAmount = ["live", "upcoming"].includes(item.id)
            ? formatOpportunityAmount(sessionCounts.net_worth_opportunity?.[item.id])
            : null;

          return (
            <BidCard key={item.id} onClick={() => navigate(item.id === "all" ? "/bids/list/all" : `/bids/${item.id}`)}>
              <IconWrapper>
                <IconBox>{item.icon}</IconBox>
                {shouldShowCount && count > 0 && <Badge>{count}</Badge>}
              </IconWrapper>
              <CardBody>
                <CardTitle>{item.title}</CardTitle>
                {opportunityAmount ? (
                  <CardSubtitle>Opportunity worth <strong>{opportunityAmount}</strong></CardSubtitle>
                ) : shouldShowCount && count > 0 && (
                  <CardSubtitle>{count} bid{count !== 1 ? "s" : ""}</CardSubtitle>
                )}
              </CardBody>
              <ChevronRight>
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                  <path d="M1 1l6 6-6 6" stroke="#EF4F5F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </ChevronRight>
            </BidCard>
          );
        })}
      </ListContainer>
    </PageWrapper>
  );
};

export default BidsFilterPage;
